import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { STATUS_META, KANBAN_ORDER, REJECT_CONFIRM } from "@/lib/pipeline";
import { Loader2 } from "lucide-react";
import { Avatar } from "@/components/db/CandidateTable";

export const kanbanCards = (candidates) => candidates.flatMap((c) => {
  const pos = c.positions || [];
  if (c.shredded) return [{ key: c.id, c, status: "rejected_shredded", position: (c.positions || []).map((x) => (typeof x === "string" ? x : x.name)).join(", ") }];
  if (!pos.length) return [{ key: c.id, c, status: c.pipeline_status || "in_process", position: "" }];
  return pos.map((x) => ({ key: `${c.id}-${x.id}`, c, posId: x.id, status: x.status || "new", position: x.name, area: x.area }));
});

export async function moveCard(card, to) {
  if (to === card.status || card.status === "rejected_shredded") return false;
  if (to === "rejected_shredded" && !window.confirm(REJECT_CONFIRM)) return false;
  const r = card.posId
    ? await api.put(`/company/candidate/${card.c.id}/positions/${card.posId}`, { status: to })
    : await api.put(`/company/candidate/${card.c.id}/pipeline-status`, { status: to });
  if (r.data.shredded) toast.success("Kandidát zamietnutý – údaje skartované a presunuté do GDPR");
  else if (to === "rejected_retain" && r.data.needs_consent) toast.info("Kandidát presunutý do Talent Pool – v profile mu vyžiadajte súhlas na uchovanie.");
  else toast.success(`Stav zmenený na „${STATUS_META[to].l}"`);
  notifyDbChanged();
  return true;
}

export const notifyDbChanged = () => window.dispatchEvent(new Event("db-changed"));

export function KanbanBoard({ cards, reload }) {
  const [over, setOver] = useState(null);
  const [busy, setBusy] = useState(null);
  const drop = async (to, key) => {
    setOver(null);
    const card = cards.find((x) => x.key === key);
    if (!card) return;
    setBusy(key);
    try { if (await moveCard(card, to)) await reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  return (
    <div className="overflow-x-auto pb-3" data-testid="kanban-board">
      <div className="flex gap-3 min-w-max 2xl:min-w-0 2xl:grid 2xl:grid-cols-6">
        {KANBAN_ORDER.map((s) => {
          const col = cards.filter((x) => x.status === s);
          return (
            <div key={s} data-testid={`kanban-col-${s}`}
              onDragOver={(e) => { e.preventDefault(); setOver(s); }} onDragLeave={() => setOver(null)}
              onDrop={(e) => drop(s, e.dataTransfer.getData("text/plain"))}
              className={`w-64 2xl:w-auto min-w-0 flex-shrink-0 rounded-3xl border p-3 transition-colors ${over === s ? "border-violet-400 bg-violet-50/60" : "border-slate-200 bg-slate-50/70"}`}>
              <div className={`flex items-center justify-between rounded-2xl border px-3 py-2 ${STATUS_META[s].card}`}>
                <span className="text-sm font-bold">{STATUS_META[s].l}</span>
                <span className="text-xs font-bold" data-testid={`kanban-count-${s}`}>{col.length}</span>
              </div>
              <div className="mt-3 space-y-2 min-h-[80px]">
                {col.map((card) => <KanbanCard key={card.key} card={card} busy={busy === card.key} onMove={(to) => drop(to, card.key)} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KanbanCard({ card, busy, onMove }) {
  const nav = useNavigate();
  const locked = card.status === "rejected_shredded";
  return (
    <div data-testid={`kanban-card-${card.key}`} draggable={!locked && !busy}
      onDragStart={(e) => e.dataTransfer.setData("text/plain", card.key)}
      className={`rounded-2xl bg-white border border-slate-200 p-3 shadow-sm ${locked ? "" : "cursor-grab active:cursor-grabbing hover:shadow-md"} transition-shadow`}>
      <div className="flex items-start gap-2">
        <Avatar c={card.c} size="h-8 w-8" />
        <div className="min-w-0 flex-1">
          <button data-testid={`kanban-open-${card.key}`} onClick={() => nav(`/company/candidate/${card.c.id}`)}
            className="block max-w-full truncate text-left text-sm font-semibold text-[#1e2a5a] hover:text-violet-700 hover:underline">{card.c.name}</button>
          <div className="truncate text-xs text-slate-500">{card.position || (card.c.areas || []).join(", ") || card.c.title || "Bez pozície"}</div>
        </div>
        {busy ? <Loader2 size={14} className="animate-spin text-violet-600" /> : typeof card.c.career_score === "number" && (
          <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-bold text-violet-700">{card.c.career_score}</span>
        )}
      </div>
      {!locked && (
        <select data-testid={`kanban-move-${card.key}`} value="" onChange={(e) => e.target.value && onMove(e.target.value)}
          className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-600 outline-none focus:border-violet-500">
          <option value="">Presunúť do…</option>
          {KANBAN_ORDER.filter((s) => s !== card.status).map((s) => <option key={s} value={s}>{STATUS_META[s].l}</option>)}
        </select>
      )}
    </div>
  );
}
